import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Camera,
    Film,
    Image as ImageIcon,
    Palette,
    User,
    Sparkles,
    Clapperboard,
    Lightbulb,
    BookOpen,
    Smile,
    Briefcase
} from 'lucide-react';

const modelingServices = [
    { id: '01', icon: Sparkles, title: 'High-Fashion Editorial', desc: 'Avant-garde concepts and striking styling tailored for magazine lookbooks.' },
    { id: '02', icon: Camera, title: 'Commercial Portfolio', desc: 'Clean, marketable photoshoots designed to attract top brands and agencies.' },
    { id: '03', icon: Palette, title: 'Creative Lookbooks', desc: 'Dynamic catalog shoots highlighting designer apparel with fluidity and poise.' },
    { id: '04', icon: Lightbulb, title: 'Creative Theme Direction', desc: 'Artistic concept planning, mood-boarding, and high-end lighting setups.' },
    { id: '05', icon: User, title: 'Comp Card Design Prints', desc: 'Curating your best angles into industry-standard composite casting cards.' },
    { id: '06', icon: Film, title: 'Runway & Motion Reels', desc: 'Cinematic walk captures showcasing your posture, movement, and presence.' },
];

const actingServices = [
    { id: '01', icon: Smile, title: 'Theatrical Headshots', desc: 'Expressive close-ups highlighting raw emotion and cinematic character depth.' },
    { id: '02', icon: Clapperboard, title: 'Casting Portfolios', desc: 'Versatile looks ranging from corporate elite to rugged antagonist personas.' },
    { id: '03', icon: Film, title: 'Monologue Showreels', desc: 'Crisp 4K video and high-fidelity audio recordings of your acting performances.' },
    { id: '04', icon: BookOpen, title: 'Character Expression Sets', desc: 'A deep-dive lookbook mapping out macro-expressions and personality ranges.' },
    { id: '05', icon: Briefcase, title: 'Industry Casting Profiles', desc: 'Professional image curation optimized for Spotlight, IMDb, and agency submissions.' },
];

const coreOfferings = [
    { id: '01', icon: Camera, title: 'Studio Headshot Sessions', desc: 'Controlled, flawless studio lighting capturing your raw, unaltered features.' },
    { id: '02', icon: Palette, title: 'Wardrobe & Style Guidance', desc: 'Collaborating with editorial stylists to select industry-appropriate textures and colors.' },
    { id: '03', icon: Lightbulb, title: 'Expression Coaching & Posing', desc: 'Expert on-set direction to draw out genuine emotion and strong, confident angles.' },
    { id: '04', icon: Sparkles, title: 'Glamour & Editorial Makeup', desc: 'High-definition hair and makeup tailored perfectly to studio flash setups.' },
    { id: '05', icon: ImageIcon, title: 'High-End Beauty Retouching', desc: 'Magazine-quality skin, hair, and garment clean-up without losing natural textures.' },
    { id: '06', icon: User, title: 'Agencies Placement Consultation', desc: 'Strategic image selection tips to grab the attention of top-tier talent scouts.' },
    { id: '07', icon: Clapperboard, title: 'Sizzle & Monologue Reels', desc: 'Bespoke acting scenes produced from script selection to final color grade.' },
    { id: '08', icon: Film, title: 'Behind The Scenes Features', desc: 'Engaging, fast-paced vertical video clips perfect for your Instagram and TikTok feeds.' },
    { id: '09', icon: BookOpen, title: 'Digital Lookbook Delivery', desc: 'Ultra-fast, password-protected private web galleries for instantaneous agency reviews.' },
];

const ModelingAndActing = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.6;
        const speedRight = 0.4;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>

            <Helmet>
                <title>Modelling & Acting Portfolio Photography in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Build a professional modelling or acting portfolio with KhultStudios in Hyderabad. We specialize in actor headshots, model portfolios, fashion photography, casting photos, audition portfolios, and creative studio shoots."
                />

                <meta
                    name="keywords"
                    content="Modelling Photography Hyderabad, Acting Portfolio Hyderabad, Model Portfolio Photography, Actor Portfolio Photography, Professional Headshots Hyderabad, Fashion Photography Hyderabad, Casting Portfolio, Audition Photography, Portfolio Shoot Hyderabad, Glamour Photography, Studio Photography Hyderabad, Celebrity Portfolio, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/modelling-acting-photography"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/modelling-acting-photography"
                />
                <meta
                    property="og:title"
                    content="Modelling & Acting Portfolio Photography in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Professional modelling and acting portfolio photography in Hyderabad. Create stunning headshots, fashion portfolios, casting photos, and audition-ready images with KhultStudios."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/modelling-acting-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Modelling & Acting Portfolio Photography | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Professional acting and modelling portfolio photography, headshots, fashion shoots, and casting portfolios in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/modelling-acting-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/khultstudio.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Modeling & Acting Photography Services
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Elevating Talent Photography   with KhultStudio
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        In the modeling and acting industries, your portfolio is your first impression and ultimate calling card. At KhultStudio, we specialize in capturing your distinct versatility, raw range, and undeniable screen presence through high-end lookbook photography and cinematic showreels. From crisp editorial frames to striking theatrical headshots, we build tools that get you noticed.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Armed with advanced lighting technology, a team of editorial stylists, and an artistic perspective, we transform single frames into dynamic casting statements designed to stop casting agents in their tracks.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        View Portfolios
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-tiktok" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                {/* FIXED: Removed display: 'none' so that the left strip animates correctly on mount */}
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=500" alt="Model Editorial 1" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=500" alt="Actor Headshot 1" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?q=80&w=500" alt="Model Lookbook 2" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1501196354995-cbb51c65aaea?q=80&w=500" alt="Actor Dramatic Profile 2" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=500" alt="Model Editorial 1 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=500" alt="Actor Headshot 1 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?q=80&w=500" alt="Model Lookbook 2 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1501196354995-cbb51c65aaea?q=80&w=500" alt="Actor Dramatic Profile 2 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/modeling/modelling-and-acting-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/modeling/modelling-and-acting-photography7.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/modeling/modelling-and-acting-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/modeling/modelling-and-acting-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/modeling/modelling-and-acting-photography4.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/modeling/modelling-and-acting-photography5.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/modeling/modelling-and-acting-photography6.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/modeling/modelling-and-acting-photography7.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* MODELLING SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">⚡ Professional Modeling Lookbooks</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        A model's book needs to prove depth, range, and adaptability. Our dedicated fashion division builds visually commanding imagery configured precisely around casting director expectations.
                    </p>
                    <div className="row g-4 mt-2">
                        {modelingServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 1 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ACTING SERVICES SECTION */}
            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">🎭 High-Impact Acting Portfolios</h2>
                        <p className="text-light-gray mt-3 max-w-2xl mx-auto">
                            Casting agencies look at thousands of submissions a day. We deliver striking headshots and monologue showreels that communicate your presence, authenticity, and micro-expressions instantly.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {actingServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CORE COMPREHENSIVE SOLUTIONS GRID */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">💼 Complete Production Deliverables</h2>
                    <div className="row g-4">
                        {coreOfferings.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ModelingAndActing;

